/*
  Subscribe and save purchase options. Markup and rules: snippets/eyenk-subscribe.liquid.

  The eye picker (assets/eyenk-rx-picker.js) owns the cart: it asks this element
  whether the customer is subscribing, for the line properties, and to apply the
  SUBSCRIPTION10 code after the lines are added. One-time purchase is the default
  and leaves the picker exactly as it was.
*/
if (!customElements.get('eyenk-subscribe')) {
  customElements.define(
    'eyenk-subscribe',
    class EyenkSubscribe extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.data = JSON.parse(this.querySelector('[data-sub-json]').textContent);
        this.arabic = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
        this.t = { ...this.data.strings.en, ...(this.arabic ? this.data.strings.ar : {}) };
        this.lensesPerBox = Number(this.dataset.lensesPerBox) || 30;
        this.details = this.querySelector('[data-sub-details]');
        this.interval = this.querySelector('[data-sub-interval]');
        this.email = this.querySelector('[data-sub-email]');
        this.error = this.querySelector('[data-sub-error]');
        this.chosenInterval = false;

        this.querySelectorAll('[data-sub-t]').forEach((el) => {
          const text = this.t[el.dataset.subT];
          if (text) el.textContent = text;
        });
        this.labelIntervals(30);

        this.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"]')) this.setMode(event.target.value);
          if (event.target === this.interval) this.chosenInterval = true;
          if (event.target === this.email) this.clearError();
        });
        this.email.addEventListener('input', () => this.clearError());
        this.setMode(this.querySelector('input[type="radio"]:checked')?.value || 'once');
        // The eye picker's script runs first; tell it to fill in the prices now.
        this.dispatchEvent(new CustomEvent('eyenk-sub:ready', { bubbles: true }));
      }

      get subscribing() {
        return this.mode === 'subscribe';
      }

      setMode(mode) {
        this.mode = mode === 'subscribe' ? 'subscribe' : 'once';
        this.querySelectorAll('[data-sub-option]').forEach((option) => {
          option.classList.toggle('is-active', option.dataset.subOption === this.mode);
        });
        this.details.hidden = !this.subscribing;
        this.clearError();
        if (this.subscribing) this.track('sub_subscribe_chosen');
      }

      // The frequency that matches the boxes chosen: one 30-pack per eye lasts 30 days.
      labelIntervals(recommended) {
        Array.from(this.interval.options).forEach((option) => {
          const days = Number(option.value);
          option.textContent = (days === recommended ? this.t.recommended : this.t.days).replace('{n}', days);
        });
        if (!this.chosenInterval) this.interval.value = String(recommended);
      }

      recommendedFor(boxesPerEye) {
        const days = Math.max(1, boxesPerEye) * this.lensesPerBox;
        return this.data.intervals.reduce((best, candidate) => (Math.abs(candidate - days) < Math.abs(best - days) ? candidate : best));
      }

      discounted(cents) {
        return Math.round((cents * (100 - this.data.percent)) / 100);
      }

      // Called by the eye picker whenever the powers or boxes change.
      setTotal(cents, boxesPerEye, money) {
        const once = this.querySelector('[data-sub-once-price]');
        const was = this.querySelector('[data-sub-was]');
        const now = this.querySelector('[data-sub-now]');
        if (once) once.textContent = money(cents);
        if (was) was.textContent = money(cents);
        if (now) now.textContent = money(this.discounted(cents));
        this.labelIntervals(this.recommendedFor(boxesPerEye));
      }

      clearError() {
        this.error.hidden = true;
        this.email.classList.remove('has-error');
      }

      validate() {
        const email = this.email.value.trim();
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return true;
        this.error.textContent = this.t.errEmail;
        this.error.hidden = false;
        this.email.classList.add('has-error');
        this.email.focus();
        this.track('sub_email_missing');
        return false;
      }

      properties() {
        const days = Number(this.interval.value) || 30;
        return {
          [this.t.property]: this.t.days.replace('{n}', days),
          _subscription_interval_days: String(days),
          _subscription_email: this.email.value.trim().toLowerCase(),
        };
      }

      /*
        Puts SUBSCRIPTION10 on the cart, unless the customer already has another
        code (the store's codes don't combine, so theirs stays; TickOS still starts
        the plan from the line property). Returns the cart sections re-rendered
        with the discount, so the drawer shows the crossed-out price at once.
      */
      async applyDiscount(sections, sectionsUrl) {
        try {
          window.localStorage && localStorage.setItem('eyenk-sub', '1');
        } catch (error) {}
        try {
          const cart = await (await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
          const codes = (cart.discount_codes || []).map((entry) => String(entry.code || '').toUpperCase());
          if (codes.some((code) => code && code !== this.data.code)) return null;
          const body = { discount: this.data.code };
          if (sections && sections.length) {
            body.sections = sections;
            body.sections_url = sectionsUrl;
          }
          const response = await fetch(`${window.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          if (!response.ok) return null;
          this.track('sub_added');
          return await response.json();
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      track(name) {
        try {
          this.tracked = this.tracked || {};
          if (this.tracked[name] || typeof window.clarity !== 'function') return;
          this.tracked[name] = true;
          window.clarity('event', name);
        } catch (error) {}
      }
    }
  );
}
